<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

import { AppIcon } from '@/components/shared'

/**
 * Searchable single-select (combobox) - built from scratch for the brand
 * (no headlessui in the dep list). Use this anywhere a vanilla <select>
 * grows past ~15 options and scanning becomes painful (locations, brands,
 * vehicle models...).
 *
 * API mirrors <Select> as closely as possible:
 *   - v-model binds the selected `id` (number | string | '')
 *   - `options` is an array of objects with at minimum `id` + `label`
 *   - `subtitleKey` lets a caller surface a secondary line (region, etc.)
 *   - `error` wires the same aria-invalid + aria-describedby plumbing
 *
 * Accessibility:
 *   - Trigger is `role="combobox"` with aria-expanded + aria-controls.
 *   - Dropdown listbox is `role="listbox"`; each option is `role="option"`.
 *   - Active option carries aria-selected + a CSS highlight tracked via
 *     `activeIndex` so the screen reader announces it while the user
 *     arrows through.
 *   - Esc closes; Enter selects the highlighted option; ArrowUp/Down
 *     navigate; clicking outside closes.
 *
 * Search:
 *   - Case + diacritic-insensitive match against `label` and `subtitle`.
 *   - Empty query shows the full list.
 *   - Filter is reset every time the panel opens.
 */
const props = defineProps({
    modelValue: {
        type: [String, Number, null],
        default: '',
    },
    /**
     * @type {{ type: ArrayConstructor, default: () => Array<{ id: number|string, label?: string, name?: string, subtitle?: string, region?: string }> }}
     * Caller can pass either `label` (preferred) or `name` (back-compat
     * with existing data shapes coming straight from Eloquent rows).
     */
    options: {
        type: Array,
        default: () => [],
    },
    /** Visible text inside the trigger when nothing is selected. */
    placeholder: {
        type: String,
        default: 'Vyber…',
    },
    /** Placeholder copy in the search input at the top of the dropdown. */
    searchPlaceholder: {
        type: String,
        default: 'Hledat…',
    },
    /** Auto-wires aria-invalid + describedby on the trigger when set. */
    error: {
        type: String,
        default: null,
    },
    /** Optional id (used for label-for) - also drives the error-id derivation. */
    id: {
        type: String,
        default: null,
    },
    /** Copy shown when the filter yields zero hits. */
    emptyLabel: {
        type: String,
        default: 'Žádné výsledky',
    },
    /**
     * Visual sizing - `default` (p-4) matches form-field recipe; `compact`
     * (px-3 py-2 text-sm) is for toolbar filter chips where the combobox
     * has to sit alongside other small controls in a horizontal bar.
     * Mirrors <Select>'s size prop so the two components stay
     * interchangeable from the caller's perspective.
     */
    size: {
        type: String,
        default: 'default',
        validator: (v) => ['default', 'compact'].includes(v),
    },
})

const emit = defineEmits(['update:modelValue', 'change'])

const open = ref(false)
const query = ref('')
const activeIndex = ref(-1)
const rootRef = ref(null)
const searchRef = ref(null)
const listRef = ref(null)

const errorId = computed(() => (props.id ? `${props.id}-error` : undefined))

function labelOf(option) {
    return option?.label ?? option?.name ?? ''
}

function subtitleOf(option) {
    return option?.subtitle ?? option?.region ?? ''
}

/**
 * Diacritic-stripping comparator. "Kvilda" should match "kvi", "Vysočina"
 * should match "vyso", etc. Pure helper - pulled out so the test surface
 * is obvious if we move this to a generic util later.
 */
function normalize(str) {
    return (str ?? '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

const filtered = computed(() => {
    const needle = normalize(query.value).trim()
    if (needle === '') return props.options
    return props.options.filter((opt) => {
        const haystack = normalize(`${labelOf(opt)} ${subtitleOf(opt)}`)
        return haystack.includes(needle)
    })
})

const selected = computed(() => props.options.find((o) => `${o.id}` === `${props.modelValue}`) ?? null)

const displayLabel = computed(() => {
    if (!selected.value) return props.placeholder
    const sub = subtitleOf(selected.value)
    return sub ? `${labelOf(selected.value)} · ${sub}` : labelOf(selected.value)
})

function openPanel() {
    if (open.value) return
    open.value = true
    query.value = ''
    // Land the highlight on whatever's currently selected so the keyboard
    // user doesn't need to ArrowDown to the right spot every time.
    const selectedIdx = props.options.findIndex((o) => `${o.id}` === `${props.modelValue}`)
    activeIndex.value = selectedIdx !== -1 ? selectedIdx : props.options.length > 0 ? 0 : -1
    nextTick(() => {
        searchRef.value?.focus()
        scrollActiveIntoView()
    })
}

function closePanel() {
    open.value = false
    activeIndex.value = -1
}

function togglePanel() {
    if (open.value) closePanel()
    else openPanel()
}

function selectOption(option) {
    emit('update:modelValue', option.id)
    emit('change', option)
    closePanel()
}

function onSearchInput(event) {
    query.value = event.target.value
    // Reset highlight to the top of the new filtered list - keeps Enter
    // intuitively selecting the first match.
    activeIndex.value = filtered.value.length > 0 ? 0 : -1
}

function moveActive(delta) {
    if (filtered.value.length === 0) return
    const next = activeIndex.value + delta
    if (next < 0) activeIndex.value = filtered.value.length - 1
    else if (next >= filtered.value.length) activeIndex.value = 0
    else activeIndex.value = next
    nextTick(scrollActiveIntoView)
}

function scrollActiveIntoView() {
    const list = listRef.value
    if (!list) return
    const el = list.querySelector(`[data-idx="${activeIndex.value}"]`)
    el?.scrollIntoView({ block: 'nearest' })
}

function onKeydown(event) {
    if (event.key === 'Escape') {
        event.preventDefault()
        closePanel()
        rootRef.value?.querySelector('[data-combobox-trigger]')?.focus()
    } else if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (!open.value) openPanel()
        else moveActive(1)
    } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (!open.value) openPanel()
        else moveActive(-1)
    } else if (event.key === 'Enter') {
        if (open.value && activeIndex.value >= 0 && activeIndex.value < filtered.value.length) {
            event.preventDefault()
            selectOption(filtered.value[activeIndex.value])
        }
    } else if (event.key === 'Home' && open.value) {
        event.preventDefault()
        activeIndex.value = 0
        scrollActiveIntoView()
    } else if (event.key === 'End' && open.value) {
        event.preventDefault()
        activeIndex.value = filtered.value.length - 1
        scrollActiveIntoView()
    }
}

/**
 * Click-outside dismiss. Bound on capture phase so a click anywhere in
 * the document (incl. another combobox elsewhere on the page) closes
 * this panel first. Removed on unmount to avoid orphaned listeners.
 */
function onDocumentClick(event) {
    if (!open.value) return
    if (rootRef.value && !rootRef.value.contains(event.target)) {
        closePanel()
    }
}

watch(open, (val) => {
    if (val) document.addEventListener('mousedown', onDocumentClick)
    else document.removeEventListener('mousedown', onDocumentClick)
})

onBeforeUnmount(() => {
    document.removeEventListener('mousedown', onDocumentClick)
})
</script>

<template>
    <div
        class="relative"
        ref="rootRef"
        @keydown="onKeydown"
    >
        <button
            class="bg-surface-container-high text-on-surface focus:border-primary-container focus:ring-primary-container/70 flex w-full items-center justify-between gap-3 rounded-md border text-left transition-all focus:ring-2 focus:outline-none"
            :class="[size === 'compact' ? 'px-3 py-2 text-sm' : 'p-4', error ? 'border-error' : 'border-zinc-700']"
            :id="id"
            type="button"
            role="combobox"
            data-combobox-trigger
            aria-haspopup="listbox"
            :aria-expanded="open"
            :aria-controls="id ? `${id}-listbox` : undefined"
            :aria-invalid="error ? 'true' : undefined"
            :aria-describedby="error ? errorId : undefined"
            @click="togglePanel"
        >
            <span
                class="truncate"
                :class="selected ? 'text-on-surface' : 'text-zinc-500'"
            >
                {{ displayLabel }}
            </span>
            <AppIcon
                class="shrink-0 text-zinc-400 transition-transform"
                name="expand_more"
                :class="{ 'rotate-180': open }"
            />
        </button>

        <!--
            Dropdown panel. Absolutely positioned beneath the trigger; max
            height + overflow-auto so long lists scroll instead of pushing
            the rest of the form down. z-30 keeps it above sibling form
            fields without colliding with modals (z-60) or toasts (z-100).
        -->
        <div
            class="bg-surface-container-high absolute z-30 mt-2 w-full overflow-hidden rounded-md border border-zinc-700 shadow-2xl shadow-black/60"
            v-if="open"
        >
            <div class="border-b border-zinc-800 p-2">
                <div class="relative">
                    <AppIcon
                        class="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-base text-zinc-500"
                        name="search"
                    />
                    <input
                        class="bg-surface-container text-on-surface focus:border-primary-container focus:ring-primary-container/60 w-full rounded-md border border-zinc-700 py-2 pr-3 pl-9 text-sm placeholder:text-zinc-500 focus:ring-1 focus:outline-none"
                        ref="searchRef"
                        type="text"
                        :placeholder="searchPlaceholder"
                        :value="query"
                        autocomplete="off"
                        role="searchbox"
                        :aria-controls="id ? `${id}-listbox` : undefined"
                        @input="onSearchInput"
                    />
                </div>
            </div>

            <ul
                class="max-h-72 overflow-y-auto py-1"
                ref="listRef"
                :id="id ? `${id}-listbox` : undefined"
                role="listbox"
            >
                <li
                    class="px-4 py-3 text-center text-sm text-zinc-500"
                    v-if="filtered.length === 0"
                >
                    {{ emptyLabel }}
                </li>
                <li
                    class="flex cursor-pointer items-center justify-between gap-3 px-4 py-2 text-sm transition-colors"
                    v-for="(option, idx) in filtered"
                    :key="option.id"
                    :data-idx="idx"
                    role="option"
                    :aria-selected="`${option.id}` === `${modelValue}`"
                    :class="
                        idx === activeIndex
                            ? 'bg-primary-container/15 text-on-surface'
                            : 'text-on-surface-variant hover:bg-surface-container-highest'
                    "
                    @mouseenter="activeIndex = idx"
                    @click="selectOption(option)"
                >
                    <span class="min-w-0 truncate">
                        <span class="text-on-surface">{{ labelOf(option) }}</span>
                        <span
                            class="text-zinc-500"
                            v-if="subtitleOf(option)"
                        >
                            · {{ subtitleOf(option) }}
                        </span>
                    </span>
                    <AppIcon
                        class="text-primary-container text-base"
                        v-if="`${option.id}` === `${modelValue}`"
                        name="check"
                    />
                </li>
            </ul>
        </div>
    </div>
</template>
